import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { FormikHelpers, useFormik } from 'formik';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  LOGIN_CONTENT_MAX_WIDTH,
  LOGIN_FORM_INITIAL_VALUES as initialValues,
  LOGIN_FORM_VALIDATION_SCHEMA as validationSchema,
  MAGIC_LINK_EMAIL_STORAGE_KEY,
} from '../../constants/auth';
import { routes } from '../../constants/routing';
import { ILoginFormState } from '../../types/auth';
import { EMAIL_INPUT } from '../../types/forms';
import {
  isExpiredMagicLinkError,
  isMagicLinkEmailMismatchError,
  isMagicLinkSignIn,
  signInWithMagicLink,
} from '../../utils/auth';
import { handleApiError } from '../../utils/ui';
import LoginPageLayout from './LoginPageLayout';

enum ViewState {
  Checking = 'checking',
  NeedsEmail = 'needs-email',
  Submitting = 'submitting',
}

const VerifyMagicLinkContainer = () => {
  const history = useHistory();
  const [view, setView] = useState<ViewState>(ViewState.Checking);

  const completeSignIn = useCallback(async (email: string) => {
    await signInWithMagicLink(email, window.location.href);
    // Sign-in succeeded — drop the stored email so it doesn't linger in localStorage.
    // authenticateFlowSaga + authorizeWorkerSaga run after this, but they don't read it.
    localStorage.removeItem(MAGIC_LINK_EMAIL_STORAGE_KEY);
  }, []);

  useEffect(() => {
    let ignore = false;

    if (!isMagicLinkSignIn(window.location.href)) {
      history.replace(routes.linkExpired);
      return undefined;
    }

    const storedEmail = localStorage.getItem(MAGIC_LINK_EMAIL_STORAGE_KEY);
    if (!storedEmail) {
      setView(ViewState.NeedsEmail);
      return undefined;
    }

    completeSignIn(storedEmail).catch((error: unknown) => {
      if (ignore) {
        return;
      }
      if (isExpiredMagicLinkError(error)) {
        history.replace(routes.linkExpired);
      } else {
        handleApiError('Could not sign you in.')(error as any);
        setView(ViewState.NeedsEmail);
      }
    });

    return () => {
      ignore = true;
    };
  }, [history, completeSignIn]);

  const onSubmit = useCallback(
    async (formValues: ILoginFormState, { setSubmitting, setFieldError }: FormikHelpers<ILoginFormState>) => {
      setView(ViewState.Submitting);
      setSubmitting(true);
      try {
        await completeSignIn(formValues.email);
      } catch (error: unknown) {
        if (isMagicLinkEmailMismatchError(error)) {
          setFieldError(EMAIL_INPUT, 'This email does not match the sign-in link.');
          setView(ViewState.NeedsEmail);
        } else if (isExpiredMagicLinkError(error)) {
          history.replace(routes.linkExpired);
        } else {
          handleApiError('Could not sign you in.')(error as any);
          setView(ViewState.NeedsEmail);
        }
      } finally {
        setSubmitting(false);
      }
    },
    [completeSignIn, history],
  );

  const { handleSubmit, handleChange, handleBlur, values, touched, errors, isValid } = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    validateOnMount: true,
  });

  return (
    <LoginPageLayout>
      {view === ViewState.NeedsEmail ? (
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{ width: '100%', maxWidth: LOGIN_CONTENT_MAX_WIDTH, mt: 8 }}
        >
          <Typography component="h1" variant="h4" align="center" sx={{ mb: 2 }}>
            Confirm your email
          </Typography>

          <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ mb: 3 }}>
            Enter the email you used to request the sign-in link
          </Typography>

          <TextField
            fullWidth
            variant="outlined"
            label="Email"
            onChange={handleChange}
            id={EMAIL_INPUT}
            name={EMAIL_INPUT}
            type="email"
            onBlur={handleBlur}
            value={values.email}
            autoComplete="username"
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email ? errors.email : ''}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={!isValid}
            fullWidth
            sx={{
              minHeight: 48,
              py: 1.5,
              px: 2.75,
            }}
          >
            Continue
          </Button>
        </Box>
      ) : (
        <CircularProgress aria-label="Signing you in" />
      )}
    </LoginPageLayout>
  );
};

export default VerifyMagicLinkContainer;
